// Central source of truth for consent/confirmation checkbox wording shown to the user.
// Each version below represents the full set of checkboxes shown at that point in time.
// Add a new `V{n}` export (and bump CURRENT_CONSENT_WORDING_VERSION) whenever the set of
// checkboxes or their wording changes materially, so consent events can be tied back to
// the exact wording the user agreed to.

export interface ConsentWordingItem {
    fieldName: string
    label: string
}

// Version 1 (live until 03/07/2026) — the original 3 checkboxes that were live on the
// signature page (CFA & LOA) before V2 superseded them. Kept for historical/audit
// reference only — no longer rendered. See signatureSentinel/index.tsx.
export const consentWordingV1: ConsentWordingItem[] = [
    {
        fieldName: 'acceptTerms',
        label: 'I confirm that I have read, understood and agree to the Conditional Fee Agreement and Letter of Authority documents above. I authorize Sentinel Legal to act on my behalf and to apply my signature to these documents for the purpose of submitting my claim.',
    },
    {
        fieldName: 'privacyPolicyConsent',
        label: "I agree to the privacy policy of Sentinel Legal and authorise them to conduct soft credit searches on my behalf through their provider, Valid8 IP Ltd, with no impact on my credit score. I also agree to Valid8 IP's privacy policy.",
    },
    {
        fieldName: 'noDuplicateClaimConsent',
        label: 'I confirm that I have not previously instructed, nor am I currently instructing, any other law firm or claims management company to pursue a motor finance claim on my behalf in relation to the same finance agreement(s). I understand that submitting duplicate claims may delay or prejudice the outcome of my case.',
    },
]

// Version 2 (live from 03/07/2026) — the checkboxes currently live on the signature
// page (CFA & LOA). See signatureSentinel/index.tsx. `privacyPolicyConsent` (the credit
// search consent) is carried over unchanged from V1 — everything else from V1 is
// dropped/superseded.
export const consentWordingV2: ConsentWordingItem[] = [
    {
        fieldName: 'privacyPolicyConsent',
        label: "I agree to the privacy policy of Sentinel Legal and authorise them to conduct soft credit searches on my behalf through their provider, Valid8 IP Ltd, with no impact on my credit score. I also agree to Valid8 IP's privacy policy.",
    },
    {
        fieldName: 'informationAccurate',
        label: 'My information is true and accurate. I confirm the details I have provided are correct and that I am the account holder, or am authorised to act, on the finance agreement(s) being claimed.',
    },
    {
        fieldName: 'understandsClaim',
        label: 'I understand what I am claiming for. I am making a claim for mis sold car finance, and I know I do not need to use a law firm to do this. I can complain to my lender directly for free, and the Financial Ombudsman Service may be able to help me.',
    },
    {
        fieldName: 'understandsFees',
        label: "I understand the fees. I have read and understood how Sentinel Legal's fees work, including the percentage, the maximum charge, VAT, and my 14 day right to cancel without charge.",
    },
    {
        fieldName: 'actingFreely',
        label: 'I am acting freely. I do not feel pressured or rushed into signing this agreement, and I have had the chance to ask questions and take independent advice before proceeding.',
    },
    {
        fieldName: 'sentinelOnlyFirm',
        label: 'Sentinel Legal is my only firm. I confirm that Sentinel Legal is the only firm instructed to handle my claim. Any instruction previously given to any other law firm or claims management company is terminated with immediate effect and has no continuing effect, and I authorise Sentinel Legal to notify them and my lender(s) of this.',
    },
]

// Version 3 replaces the Valid8 credit-search consent with ViewThru consent.
// The full ViewThru disclosure is rendered immediately above these checkboxes
// on the signature page.
export const consentWordingV3: ConsentWordingItem[] = [
    {
        fieldName: 'privacyPolicyConsent',
        label: 'I confirm that I have read and agree to the ViewThru consent information above.',
    },
    {
        fieldName: 'informationAccurate',
        label: 'My information is true and accurate. I confirm the details I have provided are correct and that I am the account holder, or am authorised to act, on the finance agreement(s) being claimed.',
    },
    {
        fieldName: 'understandsClaim',
        label: 'I understand what I am claiming for. I am making a claim for mis sold car finance, and I know I do not need to use a law firm to do this. I can complain to my lender directly for free, and the Financial Ombudsman Service may be able to help me.',
    },
    {
        fieldName: 'understandsFees',
        label: "I understand the fees. I have read and understood how Sentinel Legal's fees work, including the percentage, the maximum charge, VAT, and my 14 day right to cancel without charge.",
    },
    {
        fieldName: 'actingFreely',
        label: 'I am acting freely. I do not feel pressured or rushed into signing this agreement, and I have had the chance to ask questions and take independent advice before proceeding.',
    },
    {
        fieldName: 'sentinelOnlyFirm',
        label: 'Sentinel Legal is my only firm. I confirm that Sentinel Legal is the only firm instructed to handle my claim. Any instruction previously given to any other law firm or claims management company is terminated with immediate effect and has no continuing effect, and I authorise Sentinel Legal to notify them and my lender(s) of this.',
    },
]

export const CURRENT_CONSENT_WORDING_VERSION = '3'
